import {
  Box,
  Typography,
  Divider,
  FormControlLabel,
  Radio,
  IconButton,
  Card,
  Tooltip,
  Checkbox,
  FormGroup,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DialogContainer from "../../../../../../components/dialog-container/dialog-container";
import { useEffect, useState } from "react";
import { roleRequest } from "../../../../../../redux/roles/slice";
import { useDispatch, useSelector } from "react-redux";
import Loading from "../../../../../../components/loading/loading";
import { permissionAccessLevels } from "../../../../../../utils/Constant";
import { updateUserRolesRequest } from "../../../../../../utils/APICalls";
import { getUserDetailsRequest } from "../../../../../../redux/users/slice";
import ErrorNotification from "../../../../../../components/error-notification/error-notification";

type props = {
  showDialog: boolean;
  closeDialog: any;
  data?: any;
};

export default function EditRolesPermissions({
  showDialog,
  closeDialog,
  data,
}: props) {
  const [roles, setRoles] = useState<any[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const dispatch = useDispatch();
  const { allRoles: roleResponse } = useSelector((state: any) => state.roles);
  const { loading } = useSelector((state: any) => state.roles);

  useEffect(() => {
    if (data) {
      dispatch(roleRequest(data?.userType));
    }
  }, [data]); // eslint-disable-line

  useEffect(() => {
    if (roleResponse) {
      const allRoles = JSON.parse(JSON.stringify(roleResponse));
      allRoles.forEach((role: any) => {
        data.roles.forEach((selectedRole: any) => {
          if (role?.id === selectedRole?.id) {
            role.checked = true;
          }
        });
      });
      setRoles(allRoles);
    }
  }, [roleResponse]); // eslint-disable-line

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const allRoles = [...roles];
    allRoles[index].checked = event.target.checked;
    setRoles(allRoles);
  };

  useEffect(() => {
    if (roles.length > 0) {
      const selectedRoles = roles.filter((role) => role.checked);

      //reset permission accessLevel with actual roles response
      const allSelectedRoles = JSON.parse(JSON.stringify(selectedRoles));
      roleResponse.forEach((role: any) => {
        allSelectedRoles.forEach((selectedRole: any) => {
          if (role.id === selectedRole.id) {
            allSelectedRoles.permissions = role.permissions;
          }
        });
      });

      setSelectedRoles(allSelectedRoles);
    }
  }, [roles]); // eslint-disable-line

  useEffect(() => {
    if (selectedRoles?.length > 0) {
      const categories: any[] = [];
      const categoryNames: string[] = [];
      selectedRoles.map((role: any) =>
        role.permissions.forEach((permission: any) => {
          if (!categoryNames.includes(permission.category.name)) {
            categoryNames.push(permission.category.name);
          }
        })
      );

      categoryNames.forEach((categoryName) => {
        const filterPermissions = selectedRoles?.map((role: any) =>
          role.permissions.filter(
            (permission: any) => permission.category.name === categoryName
          )
        );
        const permissions = filterPermissions.flat(1);
        const uniquePermissions: any[] = [];
        permissions.forEach((permission) => {
          // filter unique permission with highest accessLevel
          if (uniquePermissions.length > 0) {
            let isAvailable = false;
            uniquePermissions.forEach((uniquePermission) => {
              if (permission.resourceId === uniquePermission.resourceId) {
                isAvailable = true;
                if (uniquePermission.accessLevel < permission.accessLevel)
                  uniquePermission.accessLevel = permission.accessLevel;
              }
            });
            if (!isAvailable) uniquePermissions.push(permission);
          } else {
            uniquePermissions.push(permission);
          }
        });
        categories.push({
          name: categoryName,
          permissions: uniquePermissions,
        });
      });
      setCategories(categories);
    }
  }, [selectedRoles]);

  const updateUserDetails = async () => {
    const roleIds: any[] = [];
    selectedRoles.forEach((role) => {
      roleIds.push(role.id);
    });
    if (roleIds.length === 0) {
      // if no roles is selected then show error
      setShowErrorNotification(true);
      setErrorMessage("At least one role is required");
      return;
    }

    const payloadData = { id: data?.id, roleIds };
    setShowLoading(true);
    const updateUserResponse = await updateUserRolesRequest(payloadData);
    setShowLoading(false);
    if (updateUserResponse?.status === 204) {
      dispatch(getUserDetailsRequest({ id: data?.id }));
      closeDialog();
    } else if (updateUserResponse?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(updateUserResponse?.data?.detail);
    }
  };

  return (
    <DialogContainer
      title="Edit User - Roles & Permissions"
      showDialog={showDialog}
      closeDialog={closeDialog}
      onSubmit={updateUserDetails}
    >
      <Box>
        <Typography variant="h5" sx={{ mt: 1 }}>
          Roles
        </Typography>
        <Box className="user-name-layout">
          <Typography className="label-txt">Role(s)</Typography>
          <FormGroup sx={{ display: "block", width: 620 }}>
            {roles.map((item: any, index: number) => (
              <FormControlLabel
                sx={{ width: 300 }}
                key={item.id}
                control={
                  <Checkbox
                    checked={!!item.checked}
                    onChange={(event) => handleChange(event, index)}
                  />
                }
                label={item.name}
              />
            ))}
          </FormGroup>
        </Box>
        {selectedRoles?.length > 0 && (
          <>
            <Divider sx={{ mt: 5 }} />
            <Card className="card-layout" sx={{ mt: 2 }}>
              <Box className="card-header">
                <Typography variant="h5">Permissions</Typography>
              </Box>
              <Box sx={{ mt: 3, px: 1.5 }}>
                {categories?.length > 0 ? (
                  <Box
                    sx={{
                      width: "calc(100% - 192px)",
                      marginLeft: 24,
                      display: "flex",
                    }}
                  >
                    <Typography
                      sx={{ flex: 1, textAlign: "start", fontWeight: "bold" }}
                    >
                      Permission
                    </Typography>
                    <Typography
                      sx={{ flex: 1, textAlign: "center", fontWeight: "bold" }}
                    >
                      Full Access
                    </Typography>
                    <Typography
                      sx={{ flex: 1, textAlign: "center", fontWeight: "bold" }}
                    >
                      View Only
                    </Typography>
                    <Typography
                      sx={{ flex: 1, textAlign: "center", fontWeight: "bold" }}
                    >
                      None
                    </Typography>
                  </Box>
                ) : (
                  <Typography>No Permissions Available</Typography>
                )}

                {categories?.map((category: any, index: number) => {
                  return (
                    <Box key={category.name}>
                      {index > 0 && <Divider />}
                      <Box className="user-name-layout">
                        <Typography className="label-txt" sx={{ mt: 2 }}>
                          {category.name}
                        </Typography>
                        <Box
                          display={"block"}
                          sx={{ width: "calc(100% - 192px)" }}
                        >
                          {category.permissions.map(
                            (item: any, index: number) => (
                              <Box key={item.key}>
                                <Box className="permission-row">
                                  <Box className="permission-title">
                                    <Typography>{item.name}</Typography>
                                    {index === 0 && (
                                      <Tooltip
                                        title="Description for more complicated and involved permissions"
                                        arrow
                                        placement="top"
                                      >
                                        <IconButton>
                                          <InfoOutlinedIcon color="primary" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </Box>
                                  <Radio
                                    className="permission-radio"
                                    value="full-access"
                                    name="radio-buttons"
                                    inputProps={{ "aria-label": "A" }}
                                    checked={
                                      item.accessLevel ===
                                      permissionAccessLevels.fullAccess
                                    }
                                    disabled
                                  />
                                  <Radio
                                    className="permission-radio"
                                    value="view-only"
                                    name="radio-buttons"
                                    inputProps={{ "aria-label": "B" }}
                                    checked={
                                      item.accessLevel ===
                                      permissionAccessLevels.viewOnly
                                    }
                                    disabled
                                  />
                                  <Radio
                                    className="permission-radio"
                                    value="none"
                                    name="radio-buttons"
                                    inputProps={{ "aria-label": "C" }}
                                    checked={
                                      item.accessLevel ===
                                      permissionAccessLevels.noAccess
                                    }
                                    disabled
                                  />
                                </Box>
                                {index !== category.permissions.length - 1 && (
                                  <Divider />
                                )}
                              </Box>
                            )
                          )}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Card>
          </>
        )}
      </Box>
      <ErrorNotification
        show={showErrorNotification}
        message={errorMessage}
        onClose={() => setShowErrorNotification(false)}
      />
      {(loading || showLoading) && <Loading />}
    </DialogContainer>
  );
}
